import { RadioButton } from '@deliveryhero/pd-cookbook/components/RadioButton';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';
import { FormikProps } from 'formik';
import { LocationState } from 'services/api';
import { Autocomplete } from 'src/components/Autocomplete';
import { AutocompleteOption } from 'src/components/Autocomplete/types';

import FormContainer from 'components/Forms/FormContainer';
import { TextField } from 'components/TextField';

import { FieldLabel, ILocationFormProps } from './types';
import { LocationFormValues, LocationValidationSchema } from './validation';

type CharCountProps = {
  current: number;
  total: number;
};

const CountLabel = ({ current, total }: CharCountProps) => {
  return (
    <Typography
      type="label-sm"
      color="neutral-secondary"
      style={{ textAlign: 'right' }}
    >
      {`${current}/${total} characters`}
    </Typography>
  );
};

export default function LocationForm({
  onSubmit,
  initialValues,
  buttons,
  locations,
  onQuery,
  loadingLocations,
  onSelectAddress,
  detailFormRef
}: ILocationFormProps & {
  detailFormRef: React.RefObject<FormikProps<LocationFormValues>>;
}) {
  return (
    <FormContainer
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={LocationValidationSchema}
      buttons={buttons}
      formRef={detailFormRef}
    >
      {({
        values,
        handleChange,
        setFieldValue,
        errors,
        touched
      }: FormikProps<LocationFormValues>) => (
        <>
          <TextField
            id="name"
            name="name"
            margin="normal"
            inputProps={{ maxLength: 30 }}
            label={FieldLabel.Name}
            value={values.name}
            onChange={handleChange}
            error={touched.name && errors.name ? true : false}
            helperText={touched.name && errors.name}
          />
          <CountLabel current={values.name.length} total={30} />
          <Autocomplete
            id="street_and_building"
            value={values.geolocatorStreet}
            loading={loadingLocations}
            onInputChange={(_e, value) => {
              onQuery(value);
            }}
            isOptionEqualToValue={(option, value) => {
              if (value.id == -1) return true;
              return option.name == value.name;
            }}
            options={locations}
            onChange={(_event, value) => {
              if (!value) return;
              onSelectAddress(value as AutocompleteOption);
              setFieldValue(
                'street_and_building',
                (value as AutocompleteOption).name
              );
              setFieldValue('geolocatorStreet', value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select a location"
                label={FieldLabel.Street}
                inputProps={{
                  form: { autoComplete: 'chrome-off' },
                  ...params.inputProps
                }}
                InputProps={{
                  style: { height: 'auto' },
                  ...params.InputProps
                }}
                error={
                  touched.street_and_building && errors.street_and_building
                    ? true
                    : false
                }
                helperText={
                  (touched.street_and_building && errors.street_and_building) ||
                  'Start typing and select from available address options'
                }
              />
            )}
          />
          <TextField
            id="unit_number"
            name="unit_number"
            margin="normal"
            label={FieldLabel.Unit}
            onChange={handleChange}
            value={values.unit_number}
            error={touched.unit_number && errors.unit_number ? true : false}
            helperText={touched.unit_number && errors.unit_number}
          />
          <TextField
            id="delivery_instructions"
            name="delivery_instructions"
            multiline
            margin="normal"
            label={FieldLabel.Instruction}
            onChange={handleChange}
            value={values.delivery_instructions}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '95px',
                textAlign: 'center',
                '&.Mui-focused fieldset': {
                  borderWidth: 1,
                  borderColor: '#dcdcdc'
                }
              }
            }}
          />
          <Typography type="title-sm" color="neutral-secondary">
            Location status *
          </Typography>
          <fieldset
            style={{
              minWidth: 0,
              margin: 0,
              padding: 0,
              border: 0,
              display: 'flex',
              gap: '80px'
            }}
            role="radiogroup"
          >
            <RadioButton
              id={LocationState.Active}
              name={LocationState.Active}
              label="Active"
              onChange={() => setFieldValue('state', LocationState.Active)}
              checked={values.state === LocationState.Active}
            />
            <RadioButton
              id={LocationState.Suspended}
              name={LocationState.Suspended}
              label="Inactive"
              onChange={() => setFieldValue('state', LocationState.Suspended)}
              checked={values.state === LocationState.Suspended}
            />
          </fieldset>
        </>
      )}
    </FormContainer>
  );
}
